// File: src/pages/ItemProductList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
  Label,
} from "reactstrap";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { ImDownload } from "react-icons/im";
import { FaFilter, FaPencilAlt, FaPlus, FaChevronRight } from "react-icons/fa";
import { Eye, Trash2 } from "react-feather";
import swal from "sweetalert";

// --- your app imports (paths kept per your structure) ---
// NOTE: _Get removed on purpose; using page-local fetch().
import { Deleteproductlist } from "../import/ApiEndPoint/ApiCalling";
import { Get_Producton_ProcessList } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";
import StockTrxInvoice from "../import/StockTrxInvoice";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("production-inline-css")) return;
  const style = document.createElement("style");
  style.id = "production-inline-css";
  style.innerHTML = `
    .pp-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .pp-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .pp-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1000px; }
    .pp-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .pp-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .pp-table tbody tr:hover{ background:#f9fbff; }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-view{ background:#10b981; } /* green */
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }

    .pick-list{
      border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px;
    }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const money = (n) => (Number.isFinite(+n) ? Number(n).toFixed(2) : "0.00");

/** page-local safe JSON fetcher to avoid "<!DOCTYPE ..." parse crashes */
async function safeFetchJSON(url, init = {}) {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", ...(init.headers || {}) },
    ...init,
  });
  const ct = res.headers.get("content-type") || "";
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} — ${txt.slice(0, 180)}`);
  }
  if (!ct.includes("application/json")) {
    const txt = await res.text().catch(() => "");
    throw new Error(
      `Expected JSON but got "${ct}". First 120 chars: ${txt.slice(0, 120)}`
    );
  }
  return res.json();
}

export default function ItemProductList() {
  useEffect(() => injectOnce(), []);
  const navigate = useNavigate();

  // permissions / master
  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });
  const [masterShow, setMasterShow] = useState(false);

  // data
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  // ui
  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);

  // column chooser
  const addBufferRef = useRef([]);
  const [modal, setModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [gstOnItems, setGstOnItems] = useState(0);
  const [showBill, setShowBill] = useState(false);

  // ----- Define columns -----
  const baseColumns = useMemo(
    () => [
      {
        key: "uid",
        header: "UID",
        get: (_, idx) => idx + 1,
      },
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => r?.product_name?.Product_Title,
        render: (r) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              justifyContent: "center",
            }}
          >
            <FaChevronRight size={12} />
            <span>{r?.product_name?.Product_Title || "-"}</span>
          </div>
        ),
      },
      {
        key: "productReq",
        header: "PRODUCT REQ",
        get: (r) =>
          Array.isArray(r?.productItems) ? r.productItems.length : 0,
      },
      {
        key: "productTotal",
        header: "PRODUCT TOTAL",
        get: (r) =>
          (r?.productItems || []).reduce(
            (acc, it) =>
              acc + (Number(it?.price) || 0) * (Number(it?.qty) || 0),
            0
          ),
      },
      {
        key: "gstApplied",
        header: "GST (%)",
        get: (r) => r?.gstApplied,
      },
      {
        key: "misc",
        header: "MISCELLANEOUS EXPENSES",
        get: (r) => r?.miscellaneousExpennses,
      },
      {
        key: "otherCharges",
        header: "OTHER CHARGES",
        get: (r) => r?.otherCharges,
      },
      {
        key: "discount",
        header: "DISCOUNT",
        get: (r) => r?.discount,
      },
      {
        key: "grandTotal",
        header: "GRAND TOTAL",
        get: (r) => r?.grandTotal,
      },
      {
        key: "__actions__",
        header: "ACTIONS",
        get: () => "",
        render: (r) => (
          <div className="action-chips">
            {permissions?.View && (
              <span
                className="chip chip-view"
                title="View"
                onClick={() => openView(r)}
              >
                <Eye size={14} color="white" />
              </span>
            )}
            {permissions?.Edit && (
              <span
                className="chip chip-edit"
                title="Edit"
                onClick={() =>
                  navigate(
                    `/app/freshlist/order/EditProductionProcess/${r?._id}`,
                    {
                      state: r,
                    }
                  )
                }
              >
                <FaPencilAlt size={14} color="white" />
              </span>
            )}
            {permissions?.Delete && (
              <span
                className="chip chip-delete"
                title="Delete"
                onClick={() => handleDelete(r?._id)}
              >
                <Trash2 size={14} color="white" />
              </span>
            )}
          </div>
        ),
      },
    ],
    [permissions, navigate]
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  // selected columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("ProductionProcessListCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  // ----- Fetch -----
  const reload = async (uid, db) => {
    // Send db IN THE URL (path). Also keep header "database" if your backend reads it.
    const url = `${Get_Producton_ProcessList}/${uid}/${encodeURIComponent(db)}`;
    const data = await safeFetchJSON(url, {
      headers: {
        "Content-Type": "application/json",
        database: db, // keep if server expects it in headers as well
      },
    });
    const list = Array.isArray(data?.Production) ? data.Production : [];
    setRows(list);
    setAllRows(list);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

        const inside = CheckPermission("Items");
        if (inside && typeof inside === "object") {
          setPermissions((p) => ({ ...p, ...inside }));
        }

        await reload(user?._id, user?.database);
      } catch (e) {
        console.error("[ItemProductList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load products in process. Check console for details."
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ----- Filtered rows -----
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return rows.filter((r, idx) =>
      searchCols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  // ----- Actions -----
  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        Deleteproductlist(id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  };

  const openView = (row) => {
    setViewRow(row);
    const items = row?.productItems || [];
    const base = items.reduce(
      (acc, it) => acc + (Number(it?.price) || 0) * (Number(it?.qty) || 0),
      0
    );
    const gst = (base * (Number(row?.gstApplied) || 0)) / 100;
    setGstOnItems(gst);
    setShowBill(false);
    setViewOpen(true);
  };

  // ----- Export -----
  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const head = [cols.map((c) => c.header)];
    const body = filteredRows.map((r, idx) =>
      cols.map((c) => toStr(c.get(r, idx)))
    );
    doc.text("Product List (Production Process)", 40, 30);
    doc.autoTable({
      head,
      body,
      startY: 50,
      styles: { fontSize: 8, cellPadding: 3 },
    });
    doc.save("ProductionProcessList.pdf");
  };

  const exportXLSX = (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r, idx) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r, idx))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // ----- Column chooser modal -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(
      "ProductionProcessListCols",
      JSON.stringify(selectedKeys)
    );
    closeModal();
  };

  // ----- Master switchers -----
  const handleParentSubmit = async () => {
    const raw = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!raw) return;
    const [id, db] = String(raw).split(" ");
    setLoading(true);
    setErrorMsg("");
    try {
      await reload(id, db);
    } catch (e) {
      setErrorMsg("Failed to load for selected SuperAdmin.");
    } finally {
      setLoading(false);
    }
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="pp-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Product List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              )}

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  className=""
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() =>
                    navigate(
                      "/views/apps/freshlist/Production/productionprocesspage"
                    )
                  }
                >
                  <FaPlus size={12} /> Production
                </Button>
              )}

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={exportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => exportXLSX("ProductionProcessList")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No products found.
              </div>
            ) : (
              <div className="pp-table-wrap">
                <table className="pp-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr key={r?._id || idx}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, idx)
                              : toStr(c.get(r, idx))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="pick-list">
                {baseColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 6,
                      background: "#fff",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="pick-list" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          marginBottom: 6,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* === View Modal === */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          {showBill ? "Bill Download" : "All Products"}
        </ModalHeader>
        <ModalBody className={showBill ? "p-1" : ""}>
          {showBill && viewRow ? (
            <StockTrxInvoice ViewOneData={viewRow} />
          ) : viewRow ? (
            <>
              <Row>
                <Col>
                  <Label>Product Name:</Label>
                  <h5>{viewRow?.product_name?.Product_Title}</h5>
                </Col>
                <Col>
                  <Label>Grand Total:</Label>
                  <h5>
                    <strong>{money(viewRow?.grandTotal)}</strong> Rs/-
                  </h5>
                </Col>
                <Col>
                  <Label>GST {viewRow?.gstApplied}%:</Label>
                  <h5>
                    <strong>{money(gstOnItems)}</strong>
                  </h5>
                </Col>
                <Col>
                  <Label>Miscellaneous Expenses:</Label>
                  <h5>
                    <strong>{money(viewRow?.miscellaneousExpennses)}</strong>{" "}
                    Rs/-
                  </h5>
                </Col>
                <Col>
                  <Label>Other Charges:</Label>
                  <h5>
                    <strong>{money(viewRow?.otherCharges)}</strong> Rs/-
                  </h5>
                </Col>
                <Col>
                  <Label>Discount:</Label>
                  <h5>
                    <strong>{money(viewRow?.discount)}</strong> Rs/-
                  </h5>
                </Col>
              </Row>

              <Row className="p-2">
                <Col>
                  <div className="d-flex justify-content-center">
                    <h4>Product List</h4>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>Price</th>
                        <th>Unit</th>
                        <th>Quantity</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewRow?.productItems || []).map((it, i) => (
                        <tr key={`${it?.productId?._id || i}-${i}`}>
                          <th scope="row">{i + 1}</th>
                          <td>{it?.productId?.Product_Title}</td>
                          <td>{money(it?.price)}</td>
                          <td>{it?.unitType}</td>
                          <td>{it?.qty}</td>
                          <td>
                            {money(
                              (Number(it?.price) || 0) * (Number(it?.qty) || 0)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
